/**
 * Analytics utility functions
 * 
 * This file provides a unified interface for analytics tracking
 * regardless of the underlying analytics provider (Plausible, Umami, etc.)
 */

import { analyticsConfig, getEnvironmentConfig } from '../config/analytics.config';
import { trackMetric } from '../services/api';

/**
 * Initialize analytics
 * 
 * This function will initialize the appropriate analytics provider
 * based on the configuration
 */
export const initAnalytics = (): void => {
  const { trackAnalytics } = getEnvironmentConfig();
  const { enabled } = analyticsConfig.umami;
  
  // Only initialize if enabled and in production (or explicitly enabled in development)
  if (!trackAnalytics || !enabled) {
    console.log('Analytics tracking disabled or not configured');
    return;
  }
  
  // Check if user has opted out of analytics
  const analyticsDisabled = localStorage.getItem('analytics-opt-out') === 'true';
  if (analyticsDisabled) {
    console.log('User has opted out of analytics tracking');
    return;
  }
  
  // Load the Umami script
  loadUmamiScript();
  
  // Track initial page view
  trackPageView('app_loaded');
  
  // Track app version
  const appVersion = require('../../package.json').version;
  trackEvent('app_version', { version: appVersion });
};

/**
 * Load the Umami script
 */
const loadUmamiScript = (): void => {
  const { baseUrl, websiteId } = analyticsConfig.umami;
  
  if (!baseUrl || !websiteId) {
    console.log('Umami analytics not configured');
    return;
  }
  
  try {
    // Check if script is already loaded
    if (document.querySelector(`script[src*="${baseUrl}"]`)) {
      return;
    }
    
    // Create and configure the Umami script
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.setAttribute('data-website-id', websiteId);
    script.src = `${baseUrl}/script.js`;
    
    // Add the script to the document head
    document.head.appendChild(script);
    
    console.log('Umami analytics initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Umami analytics:', error);
  }
};

/**
 * Track a custom event
 * 
 * @param eventName - Name of the event to track
 * @param eventData - Optional data to include with the event
 */
export const trackEvent = (eventName: string, eventData?: Record<string, any>): void => {
  const { trackAnalytics } = getEnvironmentConfig();
  const { enabled } = analyticsConfig.umami;
  
  // Check if analytics should track events
  if (!trackAnalytics || !enabled) {
    return;
  }
  
  // Check if user has opted out of analytics
  const analyticsDisabled = localStorage.getItem('analytics-opt-out') === 'true';
  if (analyticsDisabled) {
    return;
  }

  // Track with local backend if available (always try to track locally regardless of opt-out for critical usage data if policy allows)
  // For now, we respect the opt-out for everything.
  if (!analyticsDisabled) {
    trackMetric(eventName, eventData);
  }

  // Track with Umami if available
  if (typeof window !== 'undefined' && window.umami) {
    try {
      if (typeof window.umami === 'function') {
        // Function form
        window.umami(eventName, eventData);
      } else {
        // Object form with track method
        const umamiObj = window.umami as { track: (eventName: string, eventData?: Record<string, any>) => void };
        if (umamiObj && typeof umamiObj.track === 'function') {
          umamiObj.track(eventName, eventData);
        }
      }
    } catch (error) {
      console.error(`Failed to track event ${eventName}:`, error);
    }
  }
};

/**
 * Track a page view
 * @param pageName The name of the page to track
 */
export const trackPageView = (pageName: string): void => {
  trackEvent('pageview', { page: pageName });
};

/**
 * Track a feature usage
 * @param featureName The name of the feature being used
 */
export const trackFeatureUsage = (featureName: string): void => {
  trackEvent('feature_used', { feature: featureName });
};

/**
 * Track a time entry action
 * @param action The action performed (create, edit, delete)
 */
export const trackTimeEntryAction = (action: 'create' | 'edit' | 'delete'): void => {
  trackEvent('time_entry_action', { action });
};

/**
 * Track a project action
 * @param action The action performed (create, edit, delete)
 */
export const trackProjectAction = (action: 'create' | 'edit' | 'delete'): void => {
  trackEvent('project_action', { action });
};

/**
 * Check if analytics are available
 */
export const isAnalyticsAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const { trackAnalytics } = getEnvironmentConfig();
  const { enabled } = analyticsConfig.umami;
  
  if (!trackAnalytics || !enabled) return false;
  
  // Check if user has opted out
  const analyticsDisabled = localStorage.getItem('analytics-opt-out') === 'true';
  if (analyticsDisabled) return false;
  
  // Check if Umami is available
  if (!window.umami) return false;
  
  // Check if it's either a function or has a track method
  return typeof window.umami === 'function' || 
    (typeof (window.umami as any).track === 'function');
};

/**
 * Set user opt-out preference for analytics
 * 
 * @param optOut - Whether the user wants to opt out of analytics
 */
export const setAnalyticsOptOut = (optOut: boolean): void => {
  if (optOut) {
    localStorage.setItem('analytics-opt-out', 'true');
  } else {
    localStorage.removeItem('analytics-opt-out');
  }
};

/**
 * Check if user has opted out of analytics
 * 
 * @returns Boolean indicating if user has opted out
 */
export const hasOptedOutOfAnalytics = (): boolean => {
  return localStorage.getItem('analytics-opt-out') === 'true';
};
